import * as React from 'react'
import { ChevronDown } from 'lucide-react'
import { Slot } from 'radix-ui'

import { cn } from '@ui/lib/utils'

const ModuleNavigationSelectContext = React.createContext<() => void>(() => undefined)

export const ModuleNavigationSelectProvider = ModuleNavigationSelectContext.Provider

export interface ModuleNavigationProps extends React.ComponentProps<'nav'> {
  'aria-label': string
}

export function ModuleNavigation({ className, ...props }: ModuleNavigationProps) {
  return <nav className={cn('space-y-2', className)} {...props} />
}

export interface ModuleNavigationGroupProps {
  label: string
  icon?: React.ReactNode
  headingLink?: React.ReactElement
  headingLinkActive?: boolean
  children?: React.ReactNode
  collapsible?: boolean
  defaultOpen?: boolean
  active?: boolean
  className?: string
}

export function ModuleNavigationGroup({
  label,
  icon,
  headingLink,
  headingLinkActive = false,
  children,
  collapsible = false,
  defaultOpen = false,
  active = false,
  className,
}: ModuleNavigationGroupProps) {
  const contentId = React.useId()
  const [open, setOpen] = React.useState(defaultOpen || !collapsible)
  const [activeState, setActiveState] = React.useState({ active, closed: false })
  // A new active period starts open, even after a manual collapse.
  if (activeState.active !== active) {
    setActiveState({ active, closed: false })
  }

  const hasChildren = React.Children.toArray(children).length > 0
  const canCollapse = collapsible && hasChildren
  const activeClosed = activeState.active === active && activeState.closed
  const isOpen = !canCollapse || (active ? !activeClosed : open)

  function toggleOpen() {
    if (active) setActiveState((current) => ({ active, closed: !current.closed }))
    else setOpen((current) => !current)
  }

  if (!hasChildren && !headingLink) return null

  const headingContent = (
    <>
      {icon ? (
        <span aria-hidden="true" className="shrink-0">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1 break-words text-left">{label}</span>
      {canCollapse ? (
        <ChevronDown
          aria-hidden="true"
          className={cn('size-4 shrink-0 transition-transform', isOpen && 'rotate-180')}
        />
      ) : null}
    </>
  )

  return (
    <section className={cn('space-y-1', className)}>
      {headingLink ? (
        <div className="flex min-w-0 items-center gap-1">
          <ModuleNavigationItem
            asChild
            kind="route"
            active={headingLinkActive}
            icon={icon}
            className="min-w-0 flex-1 items-center gap-2 font-semibold"
          >
            {headingLink}
          </ModuleNavigationItem>
          {canCollapse ? (
            <button
              type="button"
              aria-label={`${label} ${isOpen ? 'einklappen' : 'ausklappen'}`}
              aria-controls={contentId}
              aria-expanded={isOpen}
              className="flex size-9 shrink-0 items-center justify-center rounded-md outline-none transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring"
              onClick={toggleOpen}
            >
              <ChevronDown
                aria-hidden="true"
                className={cn('size-4 transition-transform', isOpen && 'rotate-180')}
              />
            </button>
          ) : null}
        </div>
      ) : collapsible ? (
        <button
          type="button"
          aria-controls={contentId}
          aria-expanded={isOpen}
          className="flex w-full min-w-0 items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold outline-none transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          onClick={toggleOpen}
        >
          {headingContent}
        </button>
      ) : (
        <h2 className="flex min-w-0 items-center gap-2 px-3 py-2 text-sm font-semibold">
          {headingContent}
        </h2>
      )}
      {hasChildren && isOpen ? (
        <div
          id={contentId}
          className="ml-3 space-y-1 border-l border-sidebar-border pl-3"
        >
          {children}
        </div>
      ) : null}
    </section>
  )
}

export interface ModuleNavigationItemProps
  extends Omit<React.ComponentProps<'button'>, 'children'> {
  children: React.ReactNode
  icon?: React.ReactNode
  badge?: React.ReactNode
  description?: React.ReactNode
  active?: boolean
  kind: 'route' | 'state'
  asChild?: boolean
}

export function ModuleNavigationItem({
  children,
  icon,
  badge,
  description,
  active = false,
  kind,
  asChild = false,
  className,
  disabled = false,
  onClick,
  type,
  ...props
}: ModuleNavigationItemProps) {
  const onSelect = React.useContext(ModuleNavigationSelectContext)
  const Component = asChild ? Slot.Root : 'button'
  const slottedChild = asChild
    ? (React.Children.only(children) as React.ReactElement<{
        children?: React.ReactNode
        onClick?: React.MouseEventHandler<HTMLElement>
      }>)
    : null
  const label = (
    <span className="min-w-0 flex-1">
      <span className="block break-words">
        {slottedChild ? slottedChild.props.children : children}
      </span>
      {description ? (
        <span className="mt-0.5 block break-words text-xs text-muted-foreground">
          {description}
        </span>
      ) : null}
    </span>
  )
  const slottableContent = slottedChild
    ? React.cloneElement(
        slottedChild,
        disabled
          ? {
              onClick: (event: React.MouseEvent<HTMLElement>) => {
                event.preventDefault()
                event.stopPropagation()
              },
            }
          : undefined,
        label,
      )
    : label

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    if (disabled) {
      event.preventDefault()
      return
    }
    onClick?.(event)
    if (!event.defaultPrevented) onSelect()
  }

  return (
    <Component
      type={asChild ? undefined : (type ?? 'button')}
      disabled={asChild ? undefined : disabled}
      aria-disabled={asChild && disabled ? true : undefined}
      aria-current={kind === 'route' && active ? 'page' : undefined}
      aria-pressed={kind === 'state' ? active : undefined}
      className={cn(
        'flex w-full min-w-0 items-start gap-3 rounded-md px-3 py-2 text-left text-sm outline-none transition-colors',
        'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring',
        active && 'bg-sidebar-accent font-medium text-sidebar-accent-foreground',
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
      onClick={handleClick}
      {...props}
    >
      {icon ? (
        <span aria-hidden="true" className="mt-0.5 shrink-0">
          {icon}
        </span>
      ) : null}
      <Slot.Slottable>{slottableContent}</Slot.Slottable>
      {badge !== undefined && badge !== null ? (
        <span className="shrink-0 rounded-full bg-sidebar-accent px-2 py-0.5 text-xs tabular-nums text-sidebar-accent-foreground">
          {badge}
        </span>
      ) : null}
    </Component>
  )
}
